import { useEffect, useMemo, useState, useCallback } from 'react'
import { useFontPairings } from 'hooks/queries/fontPairings'
import { useFonts } from 'hooks/queries/workspaces/useFonts'

interface UseFontPairingSelectionParams {
  selectedFontPairingId: number | null
  onSelectFontPairing: (fontPairingId: number) => void
  recommendedIds?: number[]
  isLoadingRecommendations?: boolean
  /** If true, will not auto-select if custom fonts exist (desktop behavior) */
  skipAutoSelectIfCustomFonts?: boolean
}

export const useFontPairingSelection = ({
  selectedFontPairingId,
  onSelectFontPairing,
  recommendedIds,
  isLoadingRecommendations = false,
  skipAutoSelectIfCustomFonts = false,
}: UseFontPairingSelectionParams) => {
  const { data: fontPairingsData, isLoading: isLoadingFontPairings } = useFontPairings()
  const { data: fontsData } = useFonts()
  const customFonts = fontsData?.fonts ?? []
  const hasCustomFonts = customFonts.length > 0

  const [didManuallyChangeFontPairing, setDidManuallyChangeFontPairing] = useState(false)

  // Get the 3 font pairings to display (recommended if available, otherwise first 3)
  const displayedFontPairings = useMemo(() => {
    if (!fontPairingsData || fontPairingsData.length === 0) return []

    // Get base font pairings: recommended ones if available, otherwise first 3
    let basePairings =
      recommendedIds && recommendedIds.length > 0
        ? recommendedIds
            .map((id) => fontPairingsData.find((fp) => fp.id === id))
            .filter((fp): fp is NonNullable<typeof fp> => !!fp)
            .slice(0, 3)
        : fontPairingsData.slice(0, 3)

    // If selected font pairing is not in the displayed ones, replace the first with it
    if (selectedFontPairingId !== null && !basePairings.some((fp) => fp.id === selectedFontPairingId)) {
      const selectedPairing = fontPairingsData.find((fp) => fp.id === selectedFontPairingId)
      if (selectedPairing) {
        basePairings = [selectedPairing, ...basePairings.slice(0, 2)]
      }
    }

    return basePairings
  }, [fontPairingsData, recommendedIds, selectedFontPairingId])

  const showLoading = isLoadingFontPairings || isLoadingRecommendations

  // Auto-select first font pairing when recommendations are ready
  useEffect(() => {
    if (showLoading) return
    if (selectedFontPairingId !== null) return
    if (skipAutoSelectIfCustomFonts && hasCustomFonts) return
    if (displayedFontPairings.length > 0) {
      onSelectFontPairing(displayedFontPairings[0].id)
    }
  }, [
    showLoading,
    selectedFontPairingId,
    hasCustomFonts,
    displayedFontPairings,
    onSelectFontPairing,
    skipAutoSelectIfCustomFonts,
  ])

  const handleSelectFontPairing = useCallback(
    (fontPairingId: number) => {
      setDidManuallyChangeFontPairing(true)
      onSelectFontPairing(fontPairingId)
    },
    [onSelectFontPairing],
  )

  return {
    fontPairingsData,
    displayedFontPairings,
    customFonts,
    hasCustomFonts,
    showLoading,
    isLoadingFontPairings,
    didManuallyChangeFontPairing,
    handleSelectFontPairing,
  }
}
